import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RefreshCw,
  CheckCircle2,
  Link2,
  Info,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import * as api from '../api';
import { DIMENSION_LABELS, DIMENSION_SINGULAR, FILE_LABELS } from '../stepCatalog';
import type {
  ReconciliationAction,
  ReconciliationCandidate,
  ReconciliationDecisions,
  ReconciliationModel,
} from '../types';

interface ReconciliationStepProps {
  code: string;
  model: ReconciliationModel | null;
  loading: boolean;
  decisions: ReconciliationDecisions;
  onChange: (updater: (prev: ReconciliationDecisions) => ReconciliationDecisions) => void;
}

/** Où une valeur apparaît (pour propager la décision à tous les fichiers). */
interface ValueLocation {
  logicalName: string;
  column: string;
  /** Valeur telle qu'écrite dans CE fichier (clé de la décision). */
  rawValue: string;
}

interface GroupedValue {
  /** Valeur affichée (première rencontrée). */
  value: string;
  status: 'matched' | 'unmatched';
  matchedLabel: string | null;
  locations: ValueLocation[];
}

interface DimensionGroup {
  dimension: string;
  creatable: boolean;
  candidates: ReconciliationCandidate[];
  values: GroupedValue[];
  /** Fichiers où la notion apparaît (libellés FR). */
  sources: string[];
}

/** Ordre d'affichage des notions. */
const DIMENSION_ORDER = ['level', 'class', 'subject', 'cycle', 'student'];

/**
 * Étape Correspondances : compare les valeurs des fichiers (niveaux, classes,
 * matières…) à ce qui existe déjà dans l'école, et regroupe PAR NOTION — une
 * seule carte « Niveaux » même si les niveaux apparaissent dans plusieurs
 * fichiers. Une décision prise sur une valeur s'applique partout où elle
 * apparaît.
 */
export function ReconciliationStep({ code, model, loading, decisions, onChange }: ReconciliationStepProps) {
  const groups = useMemo(() => buildGroups(model), [model]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  // Pas de flag de cleanup : en StrictMode le cleanup annulerait la 1ʳᵉ
  // invocation et le garde ref sauterait la 2ᵉ (même piège que l'étape EN).
  const aiRanRef = useRef<ReconciliationModel | null>(null);

  // Propage la décision à tous les fichiers où la valeur apparaît.
  const decide = (
    grouped: GroupedValue,
    patch: Partial<{ action: ReconciliationAction; target: string; source: 'ai' | 'manual'; confidence: number | null }>,
  ) => {
    onChange((prev) => {
      const next: ReconciliationDecisions = { ...prev };
      for (const loc of grouped.locations) {
        next[loc.logicalName] = { ...(next[loc.logicalName] ?? {}) };
        next[loc.logicalName][loc.column] = { ...(next[loc.logicalName][loc.column] ?? {}) };
        const current = next[loc.logicalName][loc.column][loc.rawValue] ?? { action: 'ignore' };
        next[loc.logicalName][loc.column][loc.rawValue] = {
          ...current,
          ...patch,
          source: patch.source ?? 'manual',
        };
      }
      return next;
    });
  };

  const readDecision = (grouped: GroupedValue) => {
    const loc = grouped.locations[0];
    return (
      decisions[loc.logicalName]?.[loc.column]?.[loc.rawValue] ?? {
        action: 'ignore' as ReconciliationAction,
      }
    );
  };

  // Pré-mapping IA automatique : dès qu'il y a des valeurs à décider, Gemini
  // propose un rapprochement avec l'existant (les corrections manuelles ne
  // sont jamais écrasées).
  const runAiPremap = async (manual = false) => {
    const payload = groups
      .map((group) => ({
        id: group.dimension,
        label: DIMENSION_LABELS[group.dimension] ?? group.dimension,
        creatable: group.creatable,
        values: group.values.filter((v) => v.status === 'unmatched').map((v) => v.value),
        candidates: group.candidates.map((c) => c.label),
      }))
      .filter((g) => g.values.length > 0);
    if (payload.length === 0) return;

    setAiBusy(true);
    if (manual) setAiMessage(null);
    try {
      const res = await api.premapReconciliationAi(code, payload);
      if (!res.enabled) {
        if (manual) setAiMessage('Le pré-mapping IA n’est pas activé sur ce serveur.');
        return;
      }
      const groupedByDim = new Map(groups.map((g) => [g.dimension, g]));
      let applied = 0;
      onChange((prev) => {
        const next: ReconciliationDecisions = { ...prev };
        for (const s of res.suggestions) {
          // Seuls les RAPPROCHEMENTS sont appliqués : pour une vraie
          // nouveauté, le défaut (« créer ») est plus sûr qu'une IA prudente.
          if (s.action !== 'map' || !s.target) continue;
          const group = groupedByDim.get(s.id);
          const grouped = group?.values.find((v) => v.value === s.value);
          if (!group || !grouped) continue;
          // On ne touche pas aux décisions corrigées à la main.
          const first = grouped.locations[0];
          const current = next[first.logicalName]?.[first.column]?.[first.rawValue];
          if (current?.source === 'manual') continue;
          for (const loc of grouped.locations) {
            next[loc.logicalName] = { ...(next[loc.logicalName] ?? {}) };
            next[loc.logicalName][loc.column] = { ...(next[loc.logicalName][loc.column] ?? {}) };
            next[loc.logicalName][loc.column][loc.rawValue] = {
              action: s.action,
              ...(s.target ? { target: s.target } : {}),
              source: 'ai',
              confidence: s.confidence,
            };
          }
          applied++;
        }
        return next;
      });
      setAiMessage(
        applied > 0
          ? `${applied} valeur(s) pré-remplie(s) par l’IA — vérifiez avant de continuer.`
          : manual
            ? 'Aucune suggestion applicable.'
            : null,
      );
    } catch {
      if (manual) setAiMessage('Le pré-mapping IA a échoué. Vous pouvez décider manuellement.');
    } finally {
      setAiBusy(false);
    }
  };

  useEffect(() => {
    if (!model || loading || aiRanRef.current === model) return;
    aiRanRef.current = model;
    void runAiPremap(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
        Lecture de vos fichiers et comparaison avec le contenu de l’école…
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Tout est en ordre : les valeurs de vos fichiers correspondent au contenu de l’école.
          Vous pouvez continuer.
        </AlertDescription>
      </Alert>
    );
  }

  const hasUnknown = groups.some((g) => g.values.some((v) => v.status === 'unmatched'));

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>
            Certaines valeurs de vos fichiers n’existent pas encore dans l’école. Pour chacune,
            dites-nous quoi faire : <strong>la créer</strong>, indiquer que{' '}
            <strong>c’est la même chose</strong> qu’un élément existant (juste écrit
            différemment), ou <strong>la laisser de côté</strong>. Ce qui est déjà reconnu se
            rattache tout seul.
          </span>
          {hasUnknown && (
            <Button size="sm" variant="outline" onClick={() => runAiPremap(true)} disabled={aiBusy}>
              <Sparkles className={`mr-2 h-4 w-4 ${aiBusy ? 'animate-pulse' : ''}`} />
              {aiBusy ? 'Analyse IA…' : 'Pré-remplir avec l’IA'}
            </Button>
          )}
        </AlertDescription>
      </Alert>
      {aiMessage && <p className="text-sm text-muted-foreground">{aiMessage}</p>}

      {groups.map((group) => (
        <DimensionCard key={group.dimension} group={group} read={readDecision} decide={decide} />
      ))}
    </div>
  );
}

/** Fusionne toutes les colonnes de tous les fichiers par notion (dimension). */
function buildGroups(model: ReconciliationModel | null): DimensionGroup[] {
  if (!model) return [];

  const byDimension = new Map<string, DimensionGroup & { valueByKey: Map<string, GroupedValue> }>();

  for (const [logicalName, file] of Object.entries(model.files)) {
    for (const col of file.columns) {
      let group = byDimension.get(col.dimension);
      if (!group) {
        group = {
          dimension: col.dimension,
          creatable: col.creatable,
          candidates: [],
          values: [],
          sources: [],
          valueByKey: new Map(),
        };
        byDimension.set(col.dimension, group);
      }
      group.creatable = group.creatable || col.creatable;
      const source = FILE_LABELS[logicalName] ?? logicalName;
      if (!group.sources.includes(source)) group.sources.push(source);
      for (const candidate of col.candidates) {
        if (!group.candidates.some((c) => c.label === candidate.label)) {
          group.candidates.push(candidate);
        }
      }
      for (const v of col.values) {
        const key = v.normalized || v.value.trim().toLowerCase();
        let grouped = group.valueByKey.get(key);
        if (!grouped) {
          grouped = {
            value: v.value,
            status: v.status,
            matchedLabel: v.matchedLabel ?? null,
            locations: [],
          };
          group.valueByKey.set(key, grouped);
          group.values.push(grouped);
        }
        grouped.locations.push({ logicalName, column: col.column, rawValue: v.value });
      }
    }
  }

  return Array.from(byDimension.values())
    .map((group) => {
      const { valueByKey, ...rest } = group;
      void valueByKey;
      return rest;
    })
    .sort(
      (a, b) => DIMENSION_ORDER.indexOf(a.dimension) - DIMENSION_ORDER.indexOf(b.dimension),
    );
}

function DimensionCard({
  group,
  read,
  decide,
}: {
  group: DimensionGroup;
  read: (v: GroupedValue) => {
    action: ReconciliationAction;
    target?: string;
    source?: 'ai' | 'manual';
  };
  decide: (
    v: GroupedValue,
    patch: Partial<{ action: ReconciliationAction; target: string }>,
  ) => void;
}) {
  const label = DIMENSION_LABELS[group.dimension] ?? group.dimension;
  const singular = DIMENSION_SINGULAR[group.dimension] ?? 'l’élément';
  const unknown = group.values.filter((v) => v.status === 'unmatched');
  const known = group.values.filter((v) => v.status === 'matched');
  // Ouvert d'office quand il y a des valeurs à traiter.
  const [open, setOpen] = useState(unknown.length > 0);
  const [showKnown, setShowKnown] = useState(false);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="flex items-center gap-3">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full ${
              unknown.length > 0 ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
            }`}
          >
            <Link2 className="h-5 w-5" />
          </span>
          <span>
            <span className="block text-base font-semibold">{label}</span>
            <span className="block text-sm font-normal text-muted-foreground">
              mentionnés dans : {group.sources.join(', ')}
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          {known.length > 0 && <Badge variant="success">{known.length} reconnu(e)s</Badge>}
          {unknown.length > 0 && (
            <Badge className="border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100">
              {unknown.length} à décider
            </Badge>
          )}
          {open ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
        </span>
      </button>

      {open && (
        <CardContent className="space-y-4">
          {unknown.length > 0 && (
            <div className="space-y-2">
              {unknown.map((v) => {
                const decision = read(v);
                return (
                  <div
                    key={v.value}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-amber-50/50 px-3 py-2 dark:bg-amber-950/20"
                  >
                    <span className="text-sm">
                      « <span className="font-semibold">{v.value}</span> » n’existe pas encore
                      dans l’école
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        value={decision.action}
                        onValueChange={(action) =>
                          decide(v, { action: action as ReconciliationAction })
                        }
                      >
                        <SelectTrigger className="w-56">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {group.creatable && (
                            <SelectItem value="create">➕ Créer {singular}</SelectItem>
                          )}
                          <SelectItem value="map">🔗 C’est la même chose que…</SelectItem>
                          <SelectItem value="ignore">Laisser de côté</SelectItem>
                        </SelectContent>
                      </Select>

                      {decision.action === 'map' && (
                        <Select
                          value={decision.target ?? ''}
                          onValueChange={(target) => decide(v, { target })}
                        >
                          <SelectTrigger className="w-52">
                            <SelectValue placeholder="Choisir dans l’école…" />
                          </SelectTrigger>
                          <SelectContent>
                            {group.candidates.map((c) => (
                              <SelectItem key={c.label} value={c.label}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {decision.source === 'ai' && (
                        <Badge variant="secondary" title="Suggestion IA">
                          <Sparkles className="mr-1 h-3 w-3" />
                          IA
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-muted-foreground">
                « Laisser de côté » : la valeur est importée telle quelle mais rien n’y sera
                rattaché.
              </p>
            </div>
          )}

          {known.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowKnown((s) => !s)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                {showKnown ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Voir les {known.length} valeur(s) reconnue(s)
              </button>
              {showKnown && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {known.map((v) => (
                    <Badge key={v.value} variant="secondary" className="font-normal">
                      <CheckCircle2 className="mr-1 h-3 w-3 text-green-600" />
                      {v.value}
                      {v.matchedLabel && v.matchedLabel !== v.value && (
                        <span className="text-muted-foreground">&nbsp;→ {v.matchedLabel}</span>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
